import { DialogComponent } from "./dialog.js";
import { fetchConfig } from "./utilities-v2.js";
import { morph } from "./morph.js";
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
} from "./events.js";
import {
  syncGiftQuantitiesSingleFlight,
  getCart,
  primeCartCache,
} from "./pyp-gift-manager.js";
import { syncNuggetBundlesSingleFlight } from "./nugget-bundle.js";
import { cartStateManager } from "./cart-state-manager.js";
import { cartOperationQueue } from "./cart-operation-queue.js";

/**
 * @typedef {Object} CartItemProperties
 * @property {string} [_is_pyp_gift] - Whether item is a PYP gift
 * @property {string} [_parent_item_id] - Parent item ID for gifts
 * @property {string} [_rb_id] - Recharge bundle ID
 */

/**
 * @typedef {Object} CartItem
 * @property {number} id - Line item ID
 * @property {string} key - Unique item key
 * @property {number} variant_id - Variant ID
 * @property {number} quantity - Item quantity
 * @property {string} product_type - Product type
 * @property {CartItemProperties} [properties] - Custom properties
 * @property {Object} [selling_plan_allocation] - Selling plan info
 * @property {string} handle - Item handle
 */

/**
 * @typedef {Object} Cart
 * @property {CartItem[]} items - Cart items
 * @property {number} item_count - Total item count
 * @property {number} total_price - Total price in cents
 * @property {number} total_discount - Total discount in cents
 * @property {Object[]} [cart_level_discount_applications] - Cart level discounts
 * @property {Object[]} [discount_codes] - Applied discount codes
 */

/**
 * @typedef {Object} GiftHandlerData
 * @property {string} [parentKey] - Parent item key
 * @property {string} [sellingPlanId] - Selling plan ID
 * @property {string} [quantity] - Item quantity
 * @property {string} [giftKey] - Existing gift key (for switching)
 * @property {string} [giftVariantId] - Existing gift variant ID (for switching)
 * @property {string} [parentUuid] - parent uuid identifier
 */

/**
 * Cart Drawer Component - uses section rendering pattern for updates
 * @extends {DialogComponent}
 */
class GRCartDrawerComponent extends DialogComponent {
  /** @type {boolean} */
  #loading = false;

  /** @type {Promise<void> | null} */
  #sectionRefreshPromise = null;

  /** @type {boolean} */
  #needsRefresh = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @param {Event} event */
  #handleQuantitySelectorUpdate = (event) => {
    // Event type is always QuantitySelectorUpdateEvent but comes through as Event
    this.#onQuantityChange(/** @type {any} */ (event));
  };

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    // Listen for cart updates from other components
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal,
    });

    // Listen for quantity selector updates (from cart-quantity-selector-component)
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#handleQuantitySelectorUpdate,
      { signal }
    );

    // Custom events to open/close drawer
    document.addEventListener("gr-cart:open", this.#handleOpen, { signal });
    document.addEventListener("gr-cart:close", this.#handleClose, { signal });

    // Coupon input -> enable/disable Apply button
    this.addEventListener("input", this.#handleCouponInput, { signal });
    this.#updateCouponUiState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    // Clean up state manager
    cartStateManager.abortAllOperations();
    cartStateManager.clearAllLoading();
    // Clear operation queue
    cartOperationQueue.clear(false);
  }

  /** @param {Event} event */
  #handleCouponInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.getAttribute("ref") !== "couponInput") return;
    this.#updateCouponUiState();
  };

  #updateCouponUiState() {
    const { couponInput } = this.refs;
    if (!(couponInput instanceof HTMLInputElement)) return;

    const hasCode = couponInput.value.trim().length > 0;
    couponInput.disabled = this.#loading;
    this.#updateCouponButtonState(hasCode);
  }

  /** @param {Cart} cart */
  #cartOnlyHasGiftCards(cart) {
    if (!cart?.items?.length) return false;
    return cart.items.every((item) => {
      const productType =
        typeof item.product_type === "string"
          ? item.product_type.toLowerCase()
          : "";
      return productType === "gift card";
    });
  }

  #hideCouponWarning() {
    const { couponWarning } = this.refs;
    if (couponWarning instanceof HTMLElement) {
      couponWarning.classList.add("gr-hidden");
    }
  }

  #showCouponWarning() {
    const { couponWarning } = this.refs;
    if (couponWarning instanceof HTMLElement) {
      couponWarning.classList.remove("gr-hidden");
    }
  }

  /**
   * Opens the cart drawer
   */
  async open() {
    // Refresh content if it was updated while closed
    if (this.#needsRefresh) {
      await this.#fetchAndMorphSection();
      this.#needsRefresh = false;
    }
    this.showDialog();

    const response = await fetch("/cart.js");
    const cart = await response.json();

    if (Shopify?.analytics?.publish) {
      Shopify.analytics.publish("custom_cart_viewed", { data: cart });
    }
  }

  /**
   * Closes the cart drawer
   */
  close() {
    this.closeDialog();
  }

  #handleOpen = () => {
    this.open();
  };

  #handleClose = () => {
    this.close();
  };

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error("Section id missing");
    return sectionId;
  }

  /**
   * Extracts a cart payload from a cart event when available.
   * @param {CartUpdateEvent} event
   * @returns {Cart | null}
   */
  #extractCartFromEvent(event) {
    const resourceCart = event.detail?.resource;
    const resourceWithItems = resourceCart
      ? /** @type {any} */ (resourceCart)
      : null;
    if (resourceWithItems && Array.isArray(resourceWithItems.items)) {
      return /** @type {Cart} */ (resourceWithItems);
    }

    const dataCart = event.detail?.data?.cart;
    const dataWithItems = dataCart ? /** @type {any} */ (dataCart) : null;
    if (dataWithItems && Array.isArray(dataWithItems.items)) {
      return /** @type {Cart} */ (dataWithItems);
    }

    return null;
  }

  /**
   * Primes the shared cart cache when a cart snapshot is available.
   * @param {Cart | null} cart
   */
  #primeCart(cart) {
    if (cart && Array.isArray(cart.items)) {
      primeCartCache(cart);
    }
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event
   * @param {QuantitySelectorUpdateEvent} event
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    // Abort any pending operation for this line
    cartStateManager.abortLineOperation(line);

    // Lock this line immediately (prevents spam clicking + hides number before request)
    cartStateManager.setLineLoading(line);
    this.#setLineQuantityLoading(line, true);

    // Find the item key from the DOM
    const itemKey = this.#findItemKeyByLine(line);

    if (quantity === 0) {
      return this.onLineItemRemove(line, itemKey);
    }

    this.#updateQuantity(line, quantity, itemKey);
  }

  /**
   * Finds the item key from the DOM by line number
   * @param {number} line - The 1-based line number
   * @returns {string | null} The item key or null if not found
   */
  #findItemKeyByLine(line) {
    const cartItem = this.querySelector(`.gr-cart-item[data-line="${line}"]`);
    return cartItem?.getAttribute("data-key") || null;
  }

  /**
   * Handles line item removal
   * @param {number} line - The line item index
   * @param {string | null} [itemKey] - The item key
   */
  onLineItemRemove(line, itemKey) {
    this.#updateQuantity(line, 0, itemKey);
  }

  /**
   * Handles cart update events from other components
   * @param {CartUpdateEvent} event
   */
  #handleCartUpdate = async (event) => {
    // Skip if update came from this component
    if (event.detail.sourceId === "gr-cart-drawer") return;

    const incomingCart = this.#extractCartFromEvent(event);
    this.#primeCart(incomingCart);

    const isDialogOpen = this.refs.dialog?.open;
    const sectionsHtml = event.detail.data?.sections?.[this.sectionId];

    // Only morph when dialog is open to prevent unexpected reopening
    if (isDialogOpen) {
      if (sectionsHtml) {
        this.#morphCartContainers(sectionsHtml);
        this.#updateCartQuantitySelectorButtonStates();
      } else {
        // If no section HTML provided, fetch fresh
        await this.#fetchAndMorphSection();
      }
    } else {
      // Dialog is closed - mark that we need to refresh content when drawer opens next
      // Don't update cart bubble here - let cart-icon component handle it
      this.#needsRefresh = true;
    }

    // Sync PYP gift quantities when cart updates from cart-items-component
    if (event.detail.data?.source === "cart-items-component") {
      try {
        const cart = incomingCart || (await this.#fetchCart());
        await syncGiftQuantitiesSingleFlight(cart, {
          dispatchEvent: false,
          sections: [this.sectionId],
        });
      } catch (error) {
        console.error("Error syncing gift quantities:", error);
      }
    }
  };

  /**
   * Updates the cart count bubble in the trigger button
   * @param {number} count - The cart item count
   */
  #updateCartBubble(count) {
    // Update both the trigger bubble and any header bubbles
    const bubbles = this.querySelectorAll(".cart-bubble");
    const bubbleCounts = this.querySelectorAll(".cart-bubble__text-count");

    bubbles.forEach((bubble) => {
      bubble.classList.toggle("!hidden", count === 0);
    });

    bubbleCounts.forEach((bubbleCount) => {
      bubbleCount.textContent = count < 100 ? String(count) : "99+";
      bubbleCount.classList.toggle("!hidden", count === 0);
    });
  }

  /**
   * Morphs only the cart items and footer containers (targeted updates)
   * This prevents dialog state issues by leaving the dialog shell intact
   * @param {string} sectionHtml - The full section HTML
   */
  #morphCartContainers(sectionHtml) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(sectionHtml, "text/html");

    // Morph cart items container
    const newItemsContainer = doc.querySelector(
      `#cart-items-${this.sectionId}`
    );
    const oldItemsContainer = this.querySelector(
      `#cart-items-${this.sectionId}`
    );

    if (newItemsContainer && oldItemsContainer) {
      morph(oldItemsContainer, newItemsContainer);
    }

    // Morph footer container
    const newFooterContainer = doc.querySelector(
      `#cart-footer-${this.sectionId}`
    );
    const oldFooterContainer = this.querySelector(
      `#cart-footer-${this.sectionId}`
    );

    if (newFooterContainer && oldFooterContainer) {
      morph(oldFooterContainer, newFooterContainer);
    }

    // Update empty/content visibility manually
    const newDoc = doc.querySelector(".gr-cart-drawer");
    const newItems = newDoc?.querySelectorAll(".gr-cart-item") || [];
    const itemCount = newItems.length;
    
    const { emptyState, cartContent } = this.refs;

    if (emptyState instanceof HTMLElement) {
      emptyState.classList.toggle("gr-hidden", itemCount > 0);
    }
    if (cartContent instanceof HTMLElement) {
      cartContent.classList.toggle("gr-hidden", itemCount === 0);
    }

    // Defer quantity selector updates to next frame to ensure DOM is settled
    requestAnimationFrame(() => {
      // Restore loading states from centralized manager
      this.#restoreLoadingStates();
      this.#updateCartQuantitySelectorButtonStates();
    });
  }

  /**
   * Restores loading states for lines that are still processing
   * Called after morph to re-apply visual loading states
   */
  #restoreLoadingStates() {
    const loadingLines = cartStateManager.getLoadingLines();
    for (const line of loadingLines) {
      try {
        this.#setLineQuantityLoading(line, true);
      } catch (error) {
        console.error(`Failed to restore loading state for line ${line}:`, error);
      }
    }
  }

  /**
   * Fetches and morphs the section
   */
  async #fetchAndMorphSection() {
    if (this.#sectionRefreshPromise) return this.#sectionRefreshPromise;

    this.#sectionRefreshPromise = (async () => {
      try {
        const sectionUrl = new URL(window.location.href);
        sectionUrl.searchParams.set("section_id", this.sectionId);

        const response = await fetch(sectionUrl.toString());
        const sectionHtml = await response.text();

        this.#morphCartContainers(sectionHtml);
        this.#updateCartQuantitySelectorButtonStates();
        this.#updateCouponUiState();
      } catch (error) {
        console.error("Error fetching section:", error);
      } finally {
        this.#sectionRefreshPromise = null;
      }
    })();

    return this.#sectionRefreshPromise;
  }

  /**
   * Fetches cart data from Shopify
   * @returns {Promise<Cart>}
   */
  async #fetchCart(force = false) {
    return /** @type {Promise<Cart>} */ (getCart({ force }));
  }

  /**
   * Finalizes cart update: morph UI, update bubble, dispatch event
   * @param {Cart} cart - The updated cart
   * @param {string} [sectionHtml] - Optional section HTML to morph
   * @param {Record<string, string>} [sections] - Optional sections data
   * @param {number} [itemCount] - Optional item count override
   */
  async #finalizeCartUpdate(cart, sectionHtml, sections, itemCount) {
    this.#primeCart(cart);
    const count = itemCount ?? cart.item_count;

    if (sectionHtml) {
      this.#morphCartContainers(sectionHtml);
    } else {
      await this.#fetchAndMorphSection();
    }

    this.#updateCartBubble(count);

    document.dispatchEvent(
      new CartUpdateEvent(cart, "gr-cart-drawer", {
        source: "gr-cart-drawer",
        itemCount: count,
        sections,
        cart,
      })
    );
  }

  /**
   * Syncs gifts and bundles after cart operations
   * @param {Cart} cart - Initial cart result
   * @param {Record<string, string>} [initialSections] - Initial sections from cart operation
   * @returns {Promise<{cart: Cart, sections: Record<string, string>|undefined, sectionHtml: string|undefined}>}
   */
  async #syncGiftsAndBundles(cart, initialSections) {
    let currentCart = cart;
    let mergedSections = initialSections ? { ...initialSections } : undefined;
    let sectionHtml = mergedSections?.[this.sectionId];

    // Sync PYP gift quantities
    const giftSyncResult = await syncGiftQuantitiesSingleFlight(currentCart, {
      dispatchEvent: false,
      sections: [this.sectionId],
    });

    currentCart = giftSyncResult.cart || currentCart;
    if (giftSyncResult.sections) {
      mergedSections = { ...(mergedSections || {}), ...giftSyncResult.sections };
      sectionHtml = giftSyncResult.sections[this.sectionId] || sectionHtml;
    }

    // Sync nugget bundles
    const nuggetSyncResult = await syncNuggetBundlesSingleFlight(currentCart, {
      dispatchEvent: false,
      sections: [this.sectionId],
    });

    if (nuggetSyncResult.cart) {
      currentCart = /** @type {Cart} */ (nuggetSyncResult.cart);
    }
    if (nuggetSyncResult.sections) {
      mergedSections = { ...(mergedSections || {}), ...nuggetSyncResult.sections };
      sectionHtml = nuggetSyncResult.sections[this.sectionId] || sectionHtml;
    }

    // Handle case where nugget sync made changes but didn't return sections
    if (nuggetSyncResult.updated && !nuggetSyncResult.sections) {
      sectionHtml = undefined;
    }

    return { cart: currentCart, sections: mergedSections, sectionHtml };
  }

  /**
   * Updates coupon UI state (button styling and enabled/disabled)
   * @param {boolean} [hasCode] - Whether coupon input has a value
   */
  #updateCouponButtonState(hasCode) {
    const { couponApplyBtn } = this.refs;
    if (!(couponApplyBtn instanceof HTMLButtonElement)) return;

    const isEnabled = hasCode && !this.#loading;
    couponApplyBtn.disabled = this.#loading;

    couponApplyBtn.classList.toggle("bg-yolk-500", isEnabled);
    couponApplyBtn.classList.toggle("text-night-800", isEnabled);
    couponApplyBtn.classList.toggle("hover:bg-yolk-300", isEnabled);
    couponApplyBtn.classList.toggle("bg-grayscale-coal-100", !isEnabled);
    couponApplyBtn.classList.toggle("text-grayscale-coal-400", !isEnabled);
  }

  /**
   * Updates cart item quantity using section rendering
   * Uses item key (id) when available, falls back to line number
   * Wrapped in operation queue to prevent race conditions
   * @param {number} line - The 1-based line number
   * @param {number} quantity - The new quantity
   * @param {string | null} [itemKey] - The item key
   */
  async #updateQuantity(line, quantity, itemKey) {
    // Queue this operation to serialize cart updates
    return cartOperationQueue.enqueue(
      () => this.#executeUpdateQuantity(line, quantity, itemKey),
      { id: `update-line-${line}`, priority: 1 }
    );
  }

  /**
   * Executes the actual quantity update operation
   * @param {number} line
   * @param {number} quantity
   * @param {string | null} [itemKey]
   */
  async #executeUpdateQuantity(line, quantity, itemKey) {
    const loadingLine = line; // capture for finally block (immune to overwrites)
    this.#loading = true;
    cartStateManager.setLineLoading(line);
    this.#setLineQuantityLoading(line, true);
    this.#disableCart();

    // Create abort controller for this operation
    const abortController = cartStateManager.createLineOperationController(line);

    try {
      // Use item key (id) when available, falls back to line number
      const updatePayload = itemKey
        ? { id: itemKey, quantity }
        : { line, quantity };

      const body = JSON.stringify({
        ...updatePayload,
        sections: this.sectionId,
        sections_url: window.location.pathname,
      });

      const response = await fetch(
        Theme.routes.cart_change_url,
        {
          ...fetchConfig("json", { body }),
          signal: abortController.signal,
        }
      );
      const result = await response.json();

      if (result.errors) {
        console.error("Cart update error:", result.errors);
        return;
      }

      this.#primeCart(result);

      // Sync gifts and bundles
      const { cart: finalCart, sections: mergedSections, sectionHtml } = 
        await this.#syncGiftsAndBundles(result, result.sections);

      await this.#finalizeCartUpdate(finalCart, sectionHtml, mergedSections);
    } catch (error) {
      // Ignore AbortError (operation was cancelled intentionally)
      if (error.name === 'AbortError') {
        console.log('Cart operation cancelled, newer request in flight');
        return;
      }
      console.error("Error updating quantity:", error);
      // Force re-enable all quantity selectors on error
      this.#forceRestoreAllQuantitySelectors();
    } finally {
      this.#loading = false;
      cartStateManager.clearLineLoading(loadingLine);
      cartStateManager.clearLineOperationController(loadingLine);
      // Use try-catch to ensure state is always cleared even if DOM queries fail
      try {
        this.#setLineQuantityLoading(loadingLine, false);
      } catch (loadingError) {
        console.error("Error restoring loading state:", loadingError);
      }
      // Safety: if queue is empty, clear any stale loading states
      if (!cartOperationQueue.isProcessing && cartOperationQueue.length === 0) {
        cartStateManager.clearAllLoading();
        this.#forceRestoreAllQuantitySelectors();
      }
      this.#enableCart();
    }
  }

  /**
   * Sets the loading/disabled state of a quantity selector
   * @param {HTMLElement} qtySelector - The quantity selector element
   * @param {boolean} isLoading - Whether to show loading state
   * @param {boolean} useComponentAPI - Whether to try component API first
   */
  #setQuantitySelectorState(qtySelector, isLoading, useComponentAPI = true) {
    // Try component API first (preferred method)
    if (
      useComponentAPI &&
      "setLoading" in qtySelector &&
      typeof (/** @type {any} */ (qtySelector).setLoading) === "function"
    ) {
      /** @type {any} */ (qtySelector).setLoading(isLoading);
      return;
    }

    // Fallback: direct DOM manipulation
    const loader = qtySelector.querySelector("[data-qty-loader]");
    if (loader instanceof HTMLElement) {
      loader.classList.toggle("hidden", !isLoading);
    }

    const minus = qtySelector.querySelector('button[ref="minusButton"]');
    const plus = qtySelector.querySelector('button[ref="plusButton"]');
    if (minus instanceof HTMLButtonElement) minus.disabled = isLoading;
    if (plus instanceof HTMLButtonElement) plus.disabled = isLoading;

    qtySelector.classList.toggle("gr-cart-qty--loading", isLoading);
    qtySelector.setAttribute("aria-busy", isLoading ? "true" : "false");
  }

  /**
   * Shows/hides the quantity loader for a specific cart line.
   * Only replaces the number slot; +/- button styling remains unchanged.
   * @param {number} line
   * @param {boolean} isLoading
   */
  #setLineQuantityLoading(line, isLoading) {
    if (!line) {
      console.warn("setLineQuantityLoading called with invalid line:", line);
      return;
    }

    // Sync with centralized state
    if (isLoading) {
      cartStateManager.setLineLoading(line);
    } else {
      cartStateManager.clearLineLoading(line);
    }

    const cartItem = this.querySelector(`.gr-cart-item[data-line="${line}"]`);
    if (!(cartItem instanceof HTMLElement)) {
      console.warn(`Cart item not found for line ${line}`);
      return;
    }

    const qtySelector = cartItem.querySelector(
      "cart-quantity-selector-component.gr-cart-qty"
    );
    if (!(qtySelector instanceof HTMLElement)) {
      console.warn(`Quantity selector not found for line ${line}`);
      return;
    }

    try {
      this.#setQuantitySelectorState(qtySelector, isLoading);
    } catch (error) {
      console.error(`Error setting loading state for line ${line}:`, error);
      if (!isLoading) {
        this.#forceRestoreAllQuantitySelectors();
      }
    }
  }

  /**
   * Disables cart interactions during updates
   */
  #disableCart() {
    this.classList.add("gr-cart-disabled");
  }

  /**
   * Enables cart interactions after updates
   */
  #enableCart() {
    this.classList.remove("gr-cart-disabled");
  }

  /**
   * Updates button states for all cart quantity selector components
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = this.querySelectorAll(
      "cart-quantity-selector-component"
    );
    for (const selector of cartQuantitySelectors) {
      if (
        "updateButtonStates" in selector &&
        typeof selector.updateButtonStates === "function"
      ) {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Emergency recovery: force restore all quantity selectors to enabled state
   * Used when loading state gets stuck due to errors or race conditions
   */
  #forceRestoreAllQuantitySelectors() {
    console.warn("Force restoring all quantity selectors");
    const cartQuantitySelectors = this.querySelectorAll(
      "cart-quantity-selector-component.gr-cart-qty"
    );
    
    for (const qtySelector of cartQuantitySelectors) {
      if (!(qtySelector instanceof HTMLElement)) continue;
      try {
        this.#setQuantitySelectorState(qtySelector, false);
      } catch (error) {
        console.error("Error in force restore for selector:", error);
      }
    }

    // Also update button states based on inventory limits
    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * Handles coupon apply
   */
  async handleApplyCoupon() {
    const { couponInput, couponWarning } = this.refs;
    if (!couponInput || this.#loading) return;

    const code = /** @type {HTMLInputElement} */ (couponInput).value.trim();
    if (!code) return;

    // Queue this operation
    return cartOperationQueue.enqueue(
      () => this.#executeApplyCoupon(code),
      { id: `apply-coupon-${code}`, priority: 2 }
    );
  }

  /**
   * Executes the coupon apply operation
   * @param {string} code
   */
  async #executeApplyCoupon(code) {
    const { couponInput, couponWarning } = this.refs;
    if (!couponInput) return;

    this.#loading = true;
    this.#hideCouponWarning();
    this.#updateCouponUiState();
    this.#disableCart();

    try {
      const cartBefore = await this.#fetchCart(true);
      this.#primeCart(cartBefore);

      if (this.#cartOnlyHasGiftCards(cartBefore)) {
        this.#showCouponWarning();
        return;
      }

      await fetch("/discount/" + encodeURIComponent(code));

      const cartAfter = await this.#fetchCart(true);

      // Check if the discount code was applicable
      const appliedCode = cartAfter.discount_codes?.find(
        (dc) => (/** @type {any} */ (dc).code?.toLowerCase?.() === code.toLowerCase())
      );
      if ((/** @type {any} */ (appliedCode))?.applicable === false) {
        alert("Sorry, couldn't apply discount.");
        return;
      }

      // Clear input + warning
      /** @type {HTMLInputElement} */ (couponInput).value = "";
      this.#hideCouponWarning();

      await this.#finalizeCartUpdate(cartAfter);
    } catch (error) {
      console.error("Error applying coupon:", error);
    } finally {
      this.#loading = false;
      this.#enableCart();
      this.#updateCouponUiState();
      if (couponWarning instanceof HTMLElement) {
        // Ensure warning isn't sticky like TSX
        this.#hideCouponWarning();
      }
    }
  }

  /**
   * Handles coupon removal
   */
  async handleRemoveCoupon() {
    if (this.#loading) return;

    // Queue this operation
    return cartOperationQueue.enqueue(
      () => this.#executeRemoveCoupon(),
      { id: 'remove-coupon', priority: 2 }
    );
  }

  /**
   * Executes the coupon removal operation
   */
  async #executeRemoveCoupon() {
    const { couponRemoveBtn } = this.refs;
    if (this.#loading) return;

    this.#loading = true;
    this.#updateCouponUiState();
    this.#disableCart();

    // Disable remove button while processing
    if (couponRemoveBtn instanceof HTMLButtonElement) {
      couponRemoveBtn.disabled = true;
    }

    try {
      // Shopify clears discount codes when you POST an empty discount string
      const body = JSON.stringify({
        discount: "",
        sections: this.sectionId,
        sections_url: window.location.pathname,
      });
      await fetch(Theme.routes.cart_update_url, fetchConfig("json", { body }));

      const cart = await this.#fetchCart(true);
      await this.#finalizeCartUpdate(cart);
    } catch (error) {
      console.error("Error removing coupon:", error);
    } finally {
      this.#loading = false;
      this.#enableCart();
      this.#updateCouponUiState();
    }
  }

  /**
   * Checks if the cart only contains items that can skip the add-ons page
   * (gift boxes, gift cards, bundles, or PYP gifts).
   * @param {{ items: Array<any> }} cart
   * @returns {boolean}
   */
  #canSkipAddons(cart) {
    if (!cart.items || cart.items.length === 0) return true;
    return cart.items.every((item) => {
      const productType =
        typeof item.product_type === "string"
          ? item.product_type.toLowerCase()
          : "";
      const isGiftBox = productType === "gift box";
      const isGiftCard = item.handle === "digital-gift-card";
      const isBundle = !!(item.properties && item.properties._rb_id);
      const isPypGift = !!(item.properties && item.properties._is_pyp_gift === 'true');
      return isGiftBox || isGiftCard || isBundle || isPypGift;
    });
  }

  /**
   * Handles checkout button click
   */
  async handleCheckout() {
    const cart = await this.#fetchCart();

    if (!cart.items || cart.items.length === 0) {
      this.close();
      return;
    }

    const canSkipAddons = this.#canSkipAddons(cart);

    // Check if on addons page
    const isAddonsPage = window.location.pathname.includes(
      "/pre-checkout-add-ons"
    );

    if (canSkipAddons || isAddonsPage) {
      window.location.href = "/checkout";
    } else {
      window.location.href = "/collections/pre-checkout-add-ons";
    }
  }

  /**
   * Handles coupon input keydown
   * @param {KeyboardEvent} event
   */
  handleCouponKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      this.handleApplyCoupon();
    }
  }

  /**
   * Handles upgrading a one-time purchase to a subscription
   * @param {Object} data - Data from on:click handler
   * @param {string} data.lineKey - Cart line item key
   * @param {string} data.variantId - Variant ID
   * @param {string} data.sellingPlanId - Selling plan ID to use
   * @param {string} data.quantity - Current item quantity
   * @param {MouseEvent} _event
   */
  async handleUpgradeToSubscription(data, _event) {
    const lineKey = data?.lineKey;
    const sellingPlanId = data?.sellingPlanId;
    const quantity = parseInt(data?.quantity || "1", 10);
    const productId = data?.productId;
    const productName = data?.productName;
    const frequency = data?.frequency;

    if (!lineKey || !sellingPlanId) {
      console.error("Cannot upgrade to subscription: missing data", {
        lineKey,
        sellingPlanId,
      });
      return;
    }

    if (this.#loading) return;

    // Queue this operation
    return cartOperationQueue.enqueue(
      () => this.#executeUpgradeToSubscription(lineKey, sellingPlanId, quantity, { frequency, productId, productName }),
      { id: `upgrade-${lineKey}`, priority: 1 }
    );
  }

  /**
   * Executes the subscription upgrade operation
   * @param {string} lineKey
   * @param {string} sellingPlanId
   * @param {number} quantity
   * @param {{frequency?: string, productId?: string, productName?: string}} analytics
   */
  async #executeUpgradeToSubscription(lineKey, sellingPlanId, quantity, { frequency, productId, productName } = {}) {

    this.#loading = true;
    this.#disableCart();

    try {
      const body = JSON.stringify({
        id: lineKey,
        quantity,
        selling_plan: sellingPlanId,
        sections: this.sectionId,
        sections_url: window.location.pathname,
      });

      const response = await fetch(
        Theme.routes.cart_change_url,
        fetchConfig("json", { body })
      );
      const result = await response.json();

      if (result.errors) {
        console.error("Upgrade to subscription error:", result.errors);
        return;
      }

      // Track analytics
      const cartContext =
        window.grAnalytics?.buildCartContextFromCart?.(result) || undefined;
      window.grAnalytics?.trackSubscriptionBoxAdded?.(
        {
          frequency: frequency || undefined,
          product_id: productId,
          product_name: productName,
        },
        cartContext,
      );

      await this.#finalizeCartUpdate(
        result,
        result.sections?.[this.sectionId],
        result.sections
      );
    } catch (error) {
      console.error("Error upgrading to subscription:", error);
      // Force re-enable all quantity selectors on error
      this.#forceRestoreAllQuantitySelectors();
    } finally {
      this.#loading = false;
      this.#enableCart();
    }
  }

  /**
   * Opens the gift selection drawer for claiming a new gift
   * @param {GiftHandlerData} data - Data from on:click handler
   * @param {MouseEvent} _event
   */
  async handleClaimGift(data, _event) {
    const parentKey = data?.parentKey;
    const parentUuid = data?.parentUuid;
    const parentSellingPlanId = data?.sellingPlanId;
    const parentQuantity = data?.quantity || 1;

    if (!parentKey) {
      console.error("Cannot claim gift: missing parent key");
      return;
    }

    const cart = await this.#fetchCart();
    const canSkipAddons = this.#canSkipAddons(cart);

    const giftDrawer = /** @type {any} */ (
      document.querySelector("gift-selection-drawer")
    );

    if (giftDrawer && typeof giftDrawer.showSelectionMode === "function") {
      giftDrawer.showSelectionMode({
        parentItemKey: parentKey,
        parentUuid: parentUuid,
        parentSellingPlanId,
        parentQuantity:
          typeof parentQuantity === "number"
            ? parentQuantity
            : parseInt(parentQuantity, 10),
        skipAddonsRedirect: canSkipAddons,
      });
    }
  }

  /**
   * Opens the gift selection drawer for switching an existing gift
   * @param {GiftHandlerData} data - Data from on:click handler
   * @param {MouseEvent} _event
   */
  handleSwitchGift(data, _event) {
    const parentKey = data?.parentKey;
    const parentUuid = data?.parentUuid; // ← ADD THIS
    const existingGiftKey = data?.giftKey;
    const existingGiftVariantId = data?.giftVariantId;
    const parentQuantity = data?.quantity || 1;

    if (!parentKey || !existingGiftKey) {
      console.error("Cannot switch gift: missing keys", {
        parentKey,
        existingGiftKey,
      });
      return;
    }

    const giftDrawer = /** @type {any} */ (
      document.querySelector("gift-selection-drawer")
    );
    if (giftDrawer && typeof giftDrawer.showSelectionMode === "function") {
      giftDrawer.showSelectionMode({
        parentItemKey: parentKey,
        parentUuid: parentUuid, // ← ADD THIS
        existingGiftLineId: existingGiftKey,
        existingGiftVariantId,
        parentQuantity:
          typeof parentQuantity === "number"
            ? parentQuantity
            : parseInt(parentQuantity, 10),
      });
    }
  }

  /**
   * Handles subscription frequency change.
   *
   * IMPORTANT: When changing a selling plan, the line item's `key` changes.
   * We must use `line` (1-based index) to identify the item, not `id` (key).
   *
   * Strategy (leverages syncGiftQuantities for orphan cleanup):
   * - Update selling plan (1 call)
   * - syncGiftQuantities removes orphaned gift via batch update (efficient)
   * - If changing frequency (not removing): re-add gift with new parent key
   *
   * @param {Event} event - The change event (first param when no query params in on:change)
   */
  async handleSubscriptionChange(event) {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    const newSellingPlanId = select.value;
    const selectedOption = select.selectedOptions?.[0];
    const frequency = selectedOption?.dataset?.frequency;

    // Read data from select element's data attributes (populated by Liquid)
    const lineKey = select.dataset.lineKey;
    const lineIndex = parseInt(select.dataset.lineIndex || "0", 10);
    const giftVariantId = select.dataset.giftVariantId;
    const parentQuantity = parseInt(select.dataset.quantity || "1", 10);
    const parentVariantId = select.dataset.variantId;
    const productId = select.dataset.productId;
    const productName = select.dataset.productName;

    if (!lineIndex) {
      console.error("Cannot change subscription: missing line index");
      return;
    }

    if (this.#loading) return;

    // Queue this operation
    return cartOperationQueue.enqueue(
      () => this.#executeSubscriptionChange(
        select,
        newSellingPlanId,
        lineKey,
        lineIndex,
        giftVariantId,
        parentQuantity,
        parentVariantId,
        { frequency, productId, productName }
      ),
      { id: `subscription-change-${lineIndex}`, priority: 1 }
    );
  }

  /**
   * Executes the subscription change operation
   * @param {HTMLSelectElement} select
   * @param {string} newSellingPlanId
   * @param {string | undefined} lineKey
   * @param {number} lineIndex
   * @param {string | undefined} giftVariantId
   * @param {number} parentQuantity
   * @param {string | undefined} parentVariantId
   * @param {{frequency?: string, productId?: string, productName?: string}} analytics
   */
  async #executeSubscriptionChange(
    select,
    newSellingPlanId,
    lineKey,
    lineIndex,
    giftVariantId,
    parentQuantity,
    parentVariantId,
    { frequency, productId, productName } = {}
  ) {

    this.#loading = true;
    this.#disableCart();

    const isRemovingSubscription = newSellingPlanId === "one-time";
    const hadGift = Boolean(giftVariantId);

    try {
      // Step 1: Update the selling plan (this changes the line item's key)
      const response = await fetch(
        Theme.routes.cart_change_url,
        fetchConfig("json", {
          body: JSON.stringify({
            line: lineIndex,
            quantity: parentQuantity,
            selling_plan: isRemovingSubscription ? "" : newSellingPlanId,
            sections: this.sectionId,
            sections_url: window.location.pathname,
          }),
        })
      );
      const result = await response.json();

      if (result.errors) {
        console.error("Subscription update error:", result.errors);
        select.value = select.dataset.currentPlan || "one-time";
        return;
      }

      this.#primeCart(result);

      // Step 2: Let syncGiftQuantities handle orphaned gift removal
      const syncResult = await syncGiftQuantitiesSingleFlight(result, {
        dispatchEvent: false,
        sections: [this.sectionId],
      });

      let finalCart = syncResult.cart || result;
      let mergedSections = result.sections ? { ...result.sections } : undefined;
      const syncedSectionHtml = syncResult.sections?.[this.sectionId];
      if (syncedSectionHtml) {
        mergedSections = { ...(mergedSections || {}), ...syncResult.sections };
      }
      let sectionHtml = syncedSectionHtml || result.sections?.[this.sectionId];
      const finalItemCount = syncResult.itemCount ?? finalCart.item_count;

      // Step 3: If changing frequency (not removing sub) AND had a gift, re-add it with new parent key
      if (!isRemovingSubscription && hadGift && parentVariantId) {
        const parentSource = finalCart.items || result.items;
        const newParentItem = parentSource?.find(
          (/** @type {CartItem} */ item) =>
            String(item.variant_id) === String(parentVariantId) &&
            item.selling_plan_allocation &&
            !item.properties?._is_pyp_gift
        );

        if (newParentItem && newParentItem.key !== lineKey) {
          // Add gift with new parent key
          const formData = new FormData();
          formData.append("id", String(giftVariantId));
          formData.append("quantity", String(newParentItem.quantity));
          formData.append("properties[_is_pyp_gift]", "true");
          let parentUuid = newParentItem.properties?._line_item_id;
          if (!parentUuid) {
            parentUuid = crypto.randomUUID();
            // First update parent with UUID
            await fetch(
              Theme.routes.cart_change_url,
              fetchConfig("json", {
                body: JSON.stringify({
                  id: newParentItem.key,
                  quantity: newParentItem.quantity,
                  properties: {
                    ...(newParentItem.properties || {}),
                    _line_item_id: parentUuid,
                    _parent_product: "yes",
                  },
                }),
              })
            );
          }
          formData.append("properties[_parent_item_id]", parentUuid);
          formData.append("sections", this.sectionId);

          const addResponse = await fetch(Theme.routes.cart_add_url, {
            ...fetchConfig("javascript", { body: formData }),
            headers: {
              "X-Requested-With": "XMLHttpRequest",
              Accept: "application/json",
            },
          });
          if (!addResponse.ok) {
            const text = await addResponse.text().catch(() => "");
            console.error("Gift re-add failed:", addResponse.status, text);
            // Don't update currentPlan; fall back to normal render below.
            throw new Error("Gift re-add failed");
          }

          const addResult = await addResponse.json();
          if (addResult?.status || addResult?.errors) {
            console.error("Gift re-add error response:", addResult);
            throw new Error("Gift re-add error");
          }

          // Update current plan tracking only after successful gift re-add
          select.dataset.currentPlan = isRemovingSubscription ? "" : newSellingPlanId;

          await this.#finalizeCartUpdate(
            addResult,
            addResult.sections?.[this.sectionId],
            { ...(mergedSections || {}), ...addResult.sections }
          );
          return;
        }
      }

      // Update current plan tracking
      select.dataset.currentPlan = isRemovingSubscription ? "" : newSellingPlanId;

      // Track analytics
      const cartContext =
        window.grAnalytics?.buildCartContextFromCart?.(finalCart) || undefined;
      if (isRemovingSubscription) {
        window.grAnalytics?.trackOtpBoxAdded?.(
          {
            product_id: productId,
            product_name: productName,
          },
          cartContext,
        );
      } else {
        window.grAnalytics?.trackSubscriptionBoxAdded?.(
          {
            frequency: frequency || undefined,
            product_id: productId,
            product_name: productName,
          },
          cartContext,
        );
      }

      await this.#finalizeCartUpdate(finalCart, sectionHtml, mergedSections, finalItemCount);
    } catch (error) {
      console.error("Error changing subscription:", error);
      // Force re-enable all quantity selectors on error
      this.#forceRestoreAllQuantitySelectors();
    } finally {
      this.#loading = false;
      this.#enableCart();
    }
  }
}

if (!customElements.get("gr-cart-drawer")) {
  customElements.define("gr-cart-drawer", GRCartDrawerComponent);
}
